<template>
  <div class="conditions-dashboard__container">
    <div class="grid-shell">
      <div class="grid-header">
        <div class="grid-header__left">
          <div class="grid-title">
            <i class="fa-duotone fa-table-list"></i>
            <span>Conditions</span>
          </div>

          <div class="grid-sub">
            <span class="chip chip--count">{{ total || 0 }} total</span>
          </div>
        </div>

        <div class="grid-header__center">
          <div class="searchbox">
            <i class="fa-duotone fa-magnifying-glass searchbox__icon"></i>

            <input
              class="searchbox__input"
              type="text"
              v-model="searchProxy"
              placeholder="Search borrower, loan #, condition, address, status, LO/Proc/UW…"
            />

            <button
              v-if="searchProxy"
              class="searchbox__clear"
              type="button"
              title="Clear"
              @click="searchProxy = ''"
            >
              <i class="fa-duotone fa-xmark"></i>
            </button>
          </div>
        </div>

      
      </div>

      <div class="grid-wrap">
        <Grid
          ref="grid"
          :cell-render="cellTemplate"
          :row-render="rowRender"
          :style="{ height: '100%' }"
          :data-items="gridItems"
          :data-item-key="dataItemKey"
          :resizable="true"
          :reorderable="false"
          :sortable="true"
          :pageable="gridPageable"
          :groupable="false"
          :filterable="false"
          :sort="sort"
          :take="take"
          :total="total"
          :skip="skip"
          :columns="columns"
          @sortchange="$emit('sortchange', $event)"
          @pagechange="$emit('pagechange', $event)"
        >
          <template #conditionCell="{ props }">
            <td :class="props.className" :style="props.style">
              <div class="cell-stack cell-stack--accent">
                <div class="cell-title" :title="props.dataItem.title">
                  {{ props.dataItem.title || "—" }}
                </div>

                <div class="cell-sub clamp-2" :title="props.dataItem.attentionReason">
                  {{ props.dataItem.attentionReason || "—" }}
                </div>

                <div class="cell-meta">
                  <i class="fa-duotone fa-clock"></i>
                  {{ props.dataItem.attentionSetAt || "—" }}
                </div>
              </div>
            </td>
          </template>

          <template #borrowerCell="{ props }">
            <td :class="props.className" :style="props.style">
              <div class="cell-stack">
                <div class="borrower-top">
                  <div class="cell-title clamp-1" :title="props.dataItem.borrowerName">
                    {{ props.dataItem.borrowerName || "—" }}
                  </div>

                  <div class="money money--top" v-if="props.dataItem.loanAmount">
                    {{ formatCurrency(props.dataItem.loanAmount) }}
                  </div>
                </div>

                <div class="cell-sub">
                  Loan # {{ props.dataItem.loanNumber || "—" }}
                </div>

                <div class="cell-meta clamp-1" :title="props.dataItem.fullPropertyAddress">
                  <i class="fa-duotone fa-location-dot"></i>
                  {{ props.dataItem.fullPropertyAddress || "—" }}
                </div>
              </div>
            </td>
          </template>

          <template #teamCell="{ props }">
            <td :class="props.className" :style="props.style">
              <TeamCompactCell
                :item="props.dataItem"
                :photo-url="photoUrl"
                :is-photo-loading="isPhotoLoading"
                @open-teams="member => $emit('open-teams', member)"
                @open-email="(member, item) => $emit('open-email', member, item)"
                @img-error="id => $emit('img-error', id)"
              />
            </td>
          </template>

          <template #statusCell="{ props }">
            <td :class="props.className" :style="props.style">
              <div class="status-col">
                <div class="badge" :class="statusClass(props.dataItem)" :title="props.dataItem.msstatus">
                  {{ props.dataItem.msstatus || "—" }}
                </div>

                <div class="status-date">
                  <i class="fa-duotone fa-calendar-days"></i>
                  <span>{{ props.dataItem.estimatedClosingDate || "—" }}</span>
                </div>
              </div>
            </td>
          </template>

          <template #actionsCell="{ props }">
            <td :class="props.className" :style="props.style">
              <div class="actions-grid">
                <button
                  class="action-pill"
                  title="Open in Swift"
                  @click="$emit('open-loan', props.dataItem.loanId, props.dataItem.conditionId)"
                >
                  <i class="fa-duotone fa-arrow-up-right-from-square"></i>
                  <span>Swift</span>
                </button>

                <button
                  class="action-pill"
                  title="Open Encompass"
                  @click="$emit('open-encompass', props.dataItem.loanId)"
                >
                  <i class="fa-duotone fa-arrow-up-right-from-square"></i>
                  <span>Encompass</span>
                </button>

                <button
                  class="action-pill"
                  title="Watch video"
                  @click="$emit('open-video', props.dataItem.conditionId)"
                >
                  <i class="fa-duotone fa-video"></i>
                  <span>Video</span>
                </button>
              </div>
            </td>
          </template>
        </Grid>
      </div>
    </div>
  </div>
</template>

<script>
import { Grid, GridCellVue2 } from "@progress/kendo-vue-grid"
import TeamCompactCell from "./TeamCompactCell.vue"

export default {
  name: "ConditionsGrid",
  components: { Grid, TeamCompactCell },
  props: {
    columnsLoaded: { type: Boolean, required: true },
    gridItems: { type: Array, required: true },
    dataItemKey: { type: String, required: true },
    gridPageable: { type: Object, required: true },
    sort: { type: Array, required: true },
    take: { type: Number, required: true },
    total: { type: Number, required: true },
    skip: { type: Number, required: true },
    photoUrl: { type: Function, required: true },
    isPhotoLoading: { type: Function, required: true },
    closePillClassFn: { type: Function, required: true },
    searchText: { type: String, default: "" }
  },
  computed: {
    columns() {
      return [
        { title: "Condition", cell: "conditionCell" },
        { title: "Borrower / Loan", width: 400, cell: "borrowerCell" },
        { title: "Team", width: 140, cell: "teamCell" },
        { title: "Status", width: 160, cell: "statusCell" },
        { title: "Actions", width: 330, cell: "actionsCell" }
      ]
    },
    searchProxy: {
      get() {
        return this.searchText
      },
      set(val) {
        this.$emit("search-input", val)
      }
    }
  },
  methods: {
    cellTemplate(h, tdProps) {
      const slot = tdProps.column?.cell
      if (!slot || typeof slot !== "string") return h(GridCellVue2, { props: tdProps })
      return h(GridCellVue2, {
        props: tdProps,
        scopedSlots: {
          default: props => this.$scopedSlots[slot]?.({ props }) || null
        }
      })
    },
    rowRender(h, trProps, children) {
      const item = trProps?.dataItem
      if (!item) return h("tr", { class: trProps.className }, children)

      const urgency = this.closePillClassFn(item)
      const extraClass =
        urgency === "pill--red"
          ? "row-urgent"
          : urgency === "pill--yellow"
          ? "row-warning"
          : urgency === "pill--green"
          ? "row-good"
          : ""

      return h("tr", { class: `${trProps.className || ""} ${extraClass}` }, children)
    },
    statusClass(item) {
      const s = String(item?.msstatus || "").toLowerCase()
      if (!s) return "badge--neutral"
      if (s.includes("clear") || s.includes("approved") || s.includes("complete")) return "badge--good"
      if (s.includes("suspend") || s.includes("declin") || s.includes("denied")) return "badge--bad"
      if (s.includes("review") || s.includes("pending") || s.includes("submitted")) return "badge--warn"
      return "badge--neutral"
    },
    formatCurrency(v) {
      const n = Number(v)
      if (isNaN(n)) return "—"
      return n.toLocaleString("en-US", { style: "currency", currency: "USD" })
    }
  }
}
</script>

<style scoped>
.conditions-dashboard__container {
  padding: 0px 5px 0px 0px !important;
}

.grid-shell {
  height: 100%;
  width: 100%;
  overflow: hidden;
  position: relative;
  padding-top: 56px;
}

.grid-header {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  z-index: 5;
  display: grid;
  grid-template-columns: auto minmax(280px, 1fr) auto;
  align-items: center;
  gap: 12px;
  padding: 5px 10px 8px 10px;
  background: rgba(5, 29, 58, 0.03);

}

.grid-header__left {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 220px;
}

.grid-header__center {
  min-width: 0;
}



.right-spacer {
  width: 140px;
  height: 1px;
}

.grid-title {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  font-size: 12px;
  font-weight: 1000;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  color: var(--text-secondary-on-background);
}

.grid-title i {
  color: var(--notification-blue);
  --fa-primary-color: var(--notification-blue);
  --fa-secondary-color: rgba(40, 110, 255, 0.45);
  font-size: 13px;
}

.grid-sub {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 6px;
}

.chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 8px;
  border-radius: 999px;
  border: 1px solid rgba(5, 29, 58, 0.12);
  background: rgba(5, 29, 58, 0.04);
  font-size: 11px;
  font-weight: 950;
  color: var(--text-secondary-on-background);
  line-height: 1;
}

.chip--count {
  border-color: rgba(40, 110, 255, 0.18);
  background: rgba(40, 110, 255, 0.06);
  color: var(--text-primary-on-background);
}

.searchbox {
  position: relative;
  width: 100%;
}

.searchbox__icon {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  opacity: 0.75;
  pointer-events: none;
}

.searchbox__input {
  width: 100%;
  height: 40px;
  padding: 0 40px 0 38px;
  border: 1px solid var(--border-lt);
  border-radius: 12px;
  background: var(--background);
  color: var(--text-primary-on-background);
  font-size: 13px;
  font-weight: 800;
}

.searchbox__input:focus {
  outline: none;
  border-color: var(--primary);
  box-shadow: 0 0 0 2px rgba(5, 29, 58, 0.15);
}

.searchbox__clear {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  border: none;
  background: transparent;
  cursor: pointer;
  opacity: 0.85;
}

.searchbox__clear:hover {
  opacity: 1;
  color: var(--primary);
}

.grid-wrap {
  padding: 0;
  height: 100%;
  width: 100%;
  overflow: hidden;
}

.grid-wrap :deep(.k-grid) {
  height: 100% !important;
  border-radius: 0;
  border: none;
}

.grid-wrap :deep(.k-grid-header) {
  background: rgba(5, 29, 58, 0.03);
  border-bottom: 1px solid var(--border-ltr);
}

.grid-wrap :deep(.k-grid-header th) {
  font-size: 11px;
  font-weight: 1000;
  text-transform: uppercase;
  letter-spacing: 0.02em;
  color: var(--text-secondary-on-background);
  padding: 10px 10px !important;
}

.grid-wrap :deep(.k-grid-content) {
  background: var(--background);
}

.grid-wrap :deep(.k-grid td) {
  padding: 6px 8px !important;
  vertical-align: top;
  border-bottom: 1px solid rgba(0, 0, 0, 0.04);
}

.grid-wrap :deep(.k-grid-content tr) {
  transition: background 0.15s ease, box-shadow 0.15s ease;
}

.grid-wrap :deep(.k-grid-content tr:hover) {
  background: rgba(5, 29, 58, 0.035);
  box-shadow: inset 3px 0 0 var(--notification-blue);
}

.row-urgent {
  background: rgba(255, 0, 0, 0.04) !important;
}
.row-warning {
  background: rgba(255, 200, 0, 0.05) !important;
}
.row-good {
  background: rgba(0, 180, 60, 0.04) !important;
}

.cell-stack {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.cell-stack--accent {
  position: relative;
  padding-left: 10px;
}

.cell-stack--accent::before {
  content: "";
  position: absolute;
  left: 0;
  top: 2px;
  bottom: 2px;
  width: 4px;
  border-radius: 6px;
  background: var(--notification-blue);
}

.cell-title {
  font-weight: 1000;
  font-size: 13px;
  color: var(--text-primary-on-background);
  line-height: 1.25;
}

.cell-sub {
  font-weight: 750;
  font-size: 12px;
  color: var(--text-secondary-on-background);
}

.cell-meta {
  font-weight: 750;
  font-size: 11px;
  color: var(--text-secondary-on-background);
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.money {
  margin-top: 6px;
  font-size: 14px;
  font-weight: 1000;
  color: var(--primary);
}

.status-col {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-top: 4px;
}

.badge {
  padding: 6px 10px;
  border-radius: 12px;
  border: 1px solid rgba(5, 29, 58, 0.15);
  font-size: 11px;
  font-weight: 1000;
  background: rgba(5, 29, 58, 0.06);
  color: var(--text-primary-on-background);
}

.badge--good {
  background: rgba(0, 180, 60, 0.12);
  border-color: rgba(0, 180, 60, 0.25);
}

.badge--warn {
  background: rgba(255, 200, 0, 0.15);
  border-color: rgba(255, 200, 0, 0.3);
}

.badge--bad {
  background: rgba(255, 0, 0, 0.12);
  border-color: rgba(255, 0, 0, 0.25);
}

.badge--neutral {
  background: rgba(5, 29, 58, 0.06);
  border-color: rgba(5, 29, 58, 0.15);
}

.status-date {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  font-weight: 850;
  color: var(--text-secondary-on-background);
  padding-left: 2px;
  opacity: 0.95;
}

.actions-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 6px;
  padding-top: 2px;
}

.action-pill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: 100%;
  padding: 6px 8px;
  border-radius: 10px;
  border: 1px solid rgba(5, 29, 58, 0.12);
  background: rgba(5, 29, 58, 0.04);
  cursor: pointer;
  user-select: none;
  transition: transform 0.12s ease, background 0.12s ease, border-color 0.12s ease;
}

.action-pill span {
  font-size: 11px;
  font-weight: 1000;
  color: var(--text-primary-on-background);
  letter-spacing: 0.01em;
  line-height: 1;
  white-space: nowrap;
}

.action-pill i {
  font-size: 13px;
}

.action-pill:hover {
  background: rgba(40, 110, 255, 0.08);
  border-color: rgba(40, 110, 255, 0.22);
  transform: translateY(-1px);
}

.action-pill:active {
  transform: translateY(0px);
}

.action-pill:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(40, 110, 255, 0.22);
}

.clamp-1 {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.grid-wrap :deep(i.fa-duotone),
.grid-wrap :deep(i.fa-solid),
.grid-wrap :deep(i.fa-regular),
.grid-wrap :deep(i.fa-brands) {
  color: var(--notification-blue);
  --fa-primary-color: var(--notification-blue);
  --fa-secondary-color: rgba(40, 110, 255, 0.45);
}

.grid-wrap :deep(.cell-meta i),
.grid-wrap :deep(.status-date i) {
  color: rgba(40, 110, 255, 0.7);
  --fa-primary-color: rgba(40, 110, 255, 0.7);
  --fa-secondary-color: rgba(40, 110, 255, 0.35);
}

.borrower-top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
}

.money--top {
  margin-top: 0;
  font-size: 13px;
  font-weight: 1000;
  color: var(--primary);
  white-space: nowrap;
}

.grid-wrap :deep(.k-grid-content tr:nth-child(even)) {
  background: color-mix(in srgb, var(--background) 92%, var(--notification-blue) 5%);
}

.grid-wrap :deep(.k-grid-content tr:nth-child(odd)) {
  background: var(--background);
}
</style>
